import Cookies from "js-cookie";
import fetchAdapter from "@haverstack/axios-fetch-adapter";
import axios, { AxiosInstance } from "axios";
import { forceLogout } from "@/utilities/force-logout";
import {
  hasAnyPendingOfflineWork,
  resolveCashierId,
  syncCashierIdToCookieAndHeaders,
} from "@/utilities/sync-session.utils";
import {
  createCajaCerradaAxiosError,
  handleCajaCerradaRedirect,
  isCajaCerradaError,
  isCajaCerradaPayload,
  isOfflineSaleSyncRequest,
  type CajaCerradaApiBody,
  suppressCajaCerradaHandlingActive,
} from "@/utilities/caja-cerrada.utils";

const API_URL = import.meta.env.VITE_API_URL;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  adapter: fetchAdapter,
});

export const setAuthHeader = (token: string): void => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

export const removeAuthHeader = (): void => {
  delete apiClient.defaults.headers.common["Authorization"];
};

export const setStoreIdHeader = (storeId: string): void => {
  apiClient.defaults.headers.common["store-id"] = storeId;
};

export const removeStoreIdHeader = (): void => {
  delete apiClient.defaults.headers.common["store-id"];
};

export const setCashierIdHeader = (cashierId: string): void => {
  apiClient.defaults.headers.common["cashier-id"] = cashierId;
};

export const removeCashierIdHeader = (): void => {
  delete apiClient.defaults.headers.common["cashier-id"];
};

export const setCompanyTaxIdHeader = (companyTaxId: string): void => {
  apiClient.defaults.headers.common["company-tax-id"] = companyTaxId;
};

export const removeCompanyTaxIdHeader = (): void => {
  delete apiClient.defaults.headers.common["company-tax-id"];
};

apiClient.interceptors.request.use(
  (config) => {
    syncCashierIdToCookieAndHeaders();

    const token = Cookies.get("authToken");
    const storeId = Cookies.get("storeId");
    const cashierId = resolveCashierId();
    const companyTaxId = Cookies.get("companyTaxId");

    // Establecer Content-Type solo si no es FormData
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (storeId) {
      config.headers["store-id"] = storeId;
    }
    if (cashierId) {
      config.headers["cashier-id"] = cashierId;
    }
    if (companyTaxId) {
      config.headers["company-tax-id"] = companyTaxId;
    }

    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

// ── Formato Laravel (local/staging) ──────────────────────────────────────────
// Status 422 | body: { "error": "El RUC de la empresa es requerido" }
const LARAVEL_MISSING_HEADER_ERRORS = new Set([
  "El RUC de la empresa es requerido",
  "El store-id es requerido",
  "El cashier-id es requerido",
]);

// ── Formato API Gateway (producción) ─────────────────────────────────────────
// Status 400 | body: { "message": "company-tax-id is required", "requestUrl": "..." }
// Solo company-tax-id es obligatorio en el gateway; store-id y cashier-id los valida Laravel.
const GATEWAY_MISSING_HEADER_ERRORS = new Set([
  "company-tax-id is required",
]);

const isMissingHeaderError = (status: number, data: any): boolean => {
  if (status === 422 && LARAVEL_MISSING_HEADER_ERRORS.has(data?.error)) return true;
  if (status === 400 && GATEWAY_MISSING_HEADER_ERRORS.has(data?.message)) return true;
  return false;
};

apiClient.interceptors.response.use(
  async (response) => {
    if (
      isCajaCerradaPayload(response.data) &&
      !suppressCajaCerradaHandlingActive() &&
      !isOfflineSaleSyncRequest(response.config)
    ) {
      const payload = response.data as CajaCerradaApiBody;
      await handleCajaCerradaRedirect(payload);
      return Promise.reject(createCajaCerradaAxiosError(payload, response.config));
    }

    return response;
  },
  async (error) => {
    const { status, data } = error.response ?? {};

    if (isMissingHeaderError(status, data)) {
      const isMissingCashierId =
        data?.error === "El cashier-id es requerido";

      if (isMissingCashierId) {
        const hasPending = await hasAnyPendingOfflineWork();
        if (hasPending) {
          console.warn(
            "[Auth] cashier-id faltante con ventas pendientes; se mantiene la sesión y los datos locales."
          );
          return Promise.reject(error);
        }
      }

      if (Cookies.get("authToken")) {
        console.warn(
          `[Auth] Header de sesión faltante detectado (${status}). Cerrando sesión.`
        );
        await forceLogout();
      }
    } else if (
      isCajaCerradaError(error) &&
      !suppressCajaCerradaHandlingActive() &&
      !isOfflineSaleSyncRequest(error.config)
    ) {
      await handleCajaCerradaRedirect(error);
    }

    return Promise.reject(error);
  }
);
